import {
    h, Component, Fragment,
} from 'preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';
import { observer } from 'mobx-preact';

import {
    MAX_ENROLL_IMAGES,
    emptyFaceProfile,
    parseFaceProfile,
    enrolmentBlockedReason,
    faceRecognitionEnabled,
    faceErrorKey,
    loadFaceProfile,
} from '../../lib/faceConsent';

/*
 * Face-recognition consent. Two INDEPENDENT switches, because Art. 9 consent
 * must be purpose-specific:
 *   allowFindMyPhotos    — private; only this user ever sees the matches
 *   allowTagSuggestions  — public; others may be offered this person as a tag
 *
 * Enrolment is self-only and gated on age >= 18 server-side. This component
 * duplicates the age check for messaging only.
 *
 * Rendered through product/components-other.js: /api/faces/* is a C mount, so
 * in the Dyrejournal build the endpoints are gone and this must not render.
 *
 * SECOND gate, orthogonal to that one: customer.featureFlags.faceRecognition.
 * The main build serves many tenants (Musher, Raskepoter, Soknadsguiden,
 * LiveRace …) that have no Rekognition collection and no IAM grants, where every
 * enrolment would fail. Default OFF — self-gated here rather than at the call
 * site so no future render site can reintroduce the leak.
 */

export function selectableImages(user) {
    if (!user) {
        return [];
    }
    const candidates = [];
    if (user.image) {
        candidates.push(user.image);
    }
    if (Array.isArray(user.images)) {
        candidates.push(...user.images);
    }
    return candidates.filter((entry, index, list) => (
        entry && entry.newFilename
        && list.findIndex(other => other && other.newFilename === entry.newFilename) === index
    ));
}

export function canSubmit({ blockedReason, selected, switches }) {
    if (blockedReason) {
        return false;
    }
    if (!Array.isArray(selected) || selected.length === 0 || selected.length > MAX_ENROLL_IMAGES) {
        return false;
    }
    return !!(switches && (switches.allowFindMyPhotos || switches.allowTagSuggestions));
}

// Turning the LAST switch off is not an edit, it is an irreversible erasure of
// the face template. The user must be told before it happens, not after.
export function isWithdrawal(profile, key, value) {
    const next = { ...profile, [key]: value };
    return !next.allowFindMyPhotos && !next.allowTagSuggestions;
}

// One <Text> per key so every string carries its English default child, as the
// i18n convention requires. A dynamic id with a dynamic default cannot.
function errorText(key) {
    switch (key) {
        case 'noFace':
            return <Text id='faceConsent.errorNoFace'>We could not find a face in that photo. Try another one.</Text>;
        case 'multipleFaces':
            return <Text id='faceConsent.errorMultipleFaces'>That photo shows more than one person. Choose a photo showing only you.</Text>;
        case 'alreadyEnrolled':
            return <Text id='faceConsent.errorAlreadyEnrolled'>Face recognition is already switched on for you.</Text>;
        case 'underage':
            return <Text id='faceConsent.errorUnderage'>You must be 18 to consent to this.</Text>;
        case 'missingBirth':
            return <Text id='faceConsent.errorMissingBirth'>Add your date of birth to your profile first.</Text>;
        case 'noImages':
            return <Text id='faceConsent.errorNoImages'>Choose at least one photo.</Text>;
        case 'tooManyImages':
            return <Text id='faceConsent.errorTooManyImages' fields={{ max: MAX_ENROLL_IMAGES }}>
                Choose at most {MAX_ENROLL_IMAGES} photos.
            </Text>;
        case 'unknownImageKey':
            return <Text id='faceConsent.errorUnknownImageKey'>We could not find that photo. Reload the page and try again.</Text>;
        case 'noConsentGiven':
            return <Text id='faceConsent.errorNoConsentGiven'>Switch on at least one of the two options.</Text>;
        case 'switchesRequired':
            return <Text id='faceConsent.errorSwitchesRequired'>Something went wrong sending your choice. Try again.</Text>;
        case 'faceRecognitionDisabled':
            return <Text id='faceConsent.errorFaceRecognitionDisabled'>Face recognition is not switched on for your organisation.</Text>;
        default:
            return <Text id='faceConsent.errorGeneric'>Something went wrong. Nothing was changed. Try again.</Text>;
    }
}

@observer
class ProfileFaceConsent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            profile: emptyFaceProfile(),
            selected: [],
            saving: false,
            loaded: false,
            deleted: false,
            // 200 came back, but the Rekognition vector was NOT confirmed gone.
            // Our own records are erased; AWS-side removal is still outstanding.
            vectorPending: false,
            errorKey: null,
            // Inline confirmation. window.confirm is banned here: it blocks the
            // thread and does not render in this environment.
            confirm: null,
            pendingSwitch: null,
        };
        // Plain instance field, NOT component state: it must not trigger a
        // re-render on its own, it only guards componentDidUpdate against
        // firing an overlapping loadProfile() request.
        this.loading = false;
    }

    componentDidMount() {
        if (!this.isEnabled()) {
            return;
        }
        this.loadProfile();
    }

    // appState.currentCustomer starts as {} and is filled in later by an async
    // getCustomerByHostname call (app.js). On a cold deep-link straight to this
    // component, componentDidMount can run BEFORE the customer arrives, so
    // isEnabled() reads false and the mount-time loadProfile() is skipped.
    // @observer re-renders once currentCustomer shows up, but componentDidMount
    // does not fire a second time — without this hook a tenant that genuinely
    // has the feature on would have loaded:false forever and the render gate
    // (`!this.isEnabled() || !loaded`) would hide the panel permanently.
    componentDidUpdate() {
        if (this.isEnabled() && !this.state.loaded && !this.loading) {
            // Returned only so tests can await the load; Preact ignores the
            // return value of this lifecycle hook.
            return this.loadProfile();
        }
        return undefined;
    }

    isEnabled = () => {
        const { appState } = this.props.stores;
        return faceRecognitionEnabled(appState?.currentCustomer);
    }

    loadProfile = async () => {
        this.loading = true;
        try {
            const profile = await loadFaceProfile();
            this.setState({ profile: profile || emptyFaceProfile(), loaded: true });
        } finally {
            this.loading = false;
        }
    }

    toggleImage = (newFilename) => {
        this.setState((prev) => {
            const selected = prev.selected.includes(newFilename)
                ? prev.selected.filter(key => key !== newFilename)
                : [...prev.selected, newFilename];
            return { selected };
        });
    }

    enroll = async (switches) => {
        // Clear a stale "deleted" confirmation once the user starts a new
        // consent action — otherwise it would linger under the fresh state.
        this.setState({
            saving: true, deleted: false, vectorPending: false, errorKey: null,
        });
        const response = await util.fetchApi('/api/faces/enroll', { method: 'POST' }, {
            images: this.state.selected,
            allowFindMyPhotos: switches.allowFindMyPhotos,
            allowTagSuggestions: switches.allowTagSuggestions,
        });
        if (response?.status === 201) {
            this.setState({
                profile: parseFaceProfile(response.data), saving: false, selected: [], errorKey: null,
            });
            return;
        }
        // Keep the selection: the user's photos are still the ones they picked,
        // and re-picking them after a NO_FACE would be busywork.
        this.setState({ saving: false, errorKey: faceErrorKey(response) });
    }

    // The onChange seam. Anything that would leave BOTH switches off is a full
    // withdrawal and gets a confirmation step first; everything else goes
    // straight through.
    requestSwitch = (key, value) => {
        if (isWithdrawal(this.state.profile, key, value)) {
            this.setState({
                confirm: 'withdraw', pendingSwitch: { key, value }, errorKey: null, deleted: false,
            });
            return;
        }
        this.updateSwitch(key, value);
    }

    requestDelete = () => {
        this.setState({ confirm: 'delete', pendingSwitch: null, errorKey: null });
    }

    cancelConfirm = () => {
        this.setState({ confirm: null, pendingSwitch: null });
    }

    confirmPending = async () => {
        const { confirm, pendingSwitch } = this.state;
        this.setState({ confirm: null, pendingSwitch: null });
        if (confirm === 'delete') {
            await this.deleteProfile();
            return;
        }
        if (confirm === 'withdraw' && pendingSwitch) {
            await this.updateSwitch(pendingSwitch.key, pendingSwitch.value);
        }
    }

    updateSwitch = async (key, value) => {
        const { profile } = this.state;
        const next = { ...profile, [key]: value };
        this.setState({ saving: true, errorKey: null });
        const response = await util.fetchApi('/api/faces/profile', { method: 'PATCH' }, {
            allowFindMyPhotos: next.allowFindMyPhotos,
            allowTagSuggestions: next.allowTagSuggestions,
        });
        if (response?.status !== 200) {
            // Previously this silently reverted: the switch snapped back with
            // no explanation at all.
            this.setState({ saving: false, errorKey: faceErrorKey(response) });
            return;
        }
        const updated = parseFaceProfile(response.data);
        // Both switches off IS the teardown. The server says so by returning a
        // not-enrolled profile; acknowledge it exactly as the delete button does.
        const withdrew = !updated.enrolled;
        this.setState({
            profile: updated,
            saving: false,
            deleted: withdrew && response.vectorRemoved === true,
            vectorPending: withdrew && response.vectorRemoved !== true,
        });
    }

    deleteProfile = async () => {
        this.setState({ saving: true, errorKey: null });
        const response = await util.fetchApi('/api/faces/profile', { method: 'DELETE' }, {});
        // Only claim erasure happened on a confirmed 200. A timeout or 500 may
        // have left the row — and the Rekognition vector — live; telling the
        // user it is gone would be materially misleading for an erasure action.
        if (response?.status !== 200) {
            this.setState({ saving: false, errorKey: faceErrorKey(response) });
            return;
        }
        // A 200 only means OUR records are gone. The server reports separately
        // whether the Rekognition vector was actually removed; an unqualified
        // "your face data has been deleted" while a live biometric template
        // remains in AWS would be a false erasure claim.
        this.setState({
            profile: emptyFaceProfile(),
            saving: false,
            deleted: response.vectorRemoved === true,
            vectorPending: response.vectorRemoved !== true,
        });
    }

    render() {
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const {
            profile, selected, saving, loaded, deleted, vectorPending, errorKey, confirm,
        } = this.state;

        // Kill switch, checked at render as well as at mount: no tenant sees the
        // panel unless face recognition is provisioned and switched on for them.
        if (!this.isEnabled() || !loaded) {
            return null;
        }

        const blockedReason = enrolmentBlockedReason(user);
        const images = selectableImages(user);

        return (
            <div class='card mx-3 mt-3'>
                <div class='card-body'>
                    <h5 class='card-title'>
                        <i class='fa-duotone fa-face-viewfinder me-2' />
                        <Text id='faceConsent.title'>Face recognition</Text>
                    </h5>
                    <p class='text-body-secondary small mb-3'>
                        <Text id='faceConsent.intro'>
                            Here you choose whether we create a face template of you. The
                            template will be used so you can find pictures of yourself, and so
                            others can be offered your name when tagging. Neither is in use yet:
                            you choose now, and your choice applies from the moment the features
                            launch. It is entirely voluntary, and you can switch it off at any
                            time.
                        </Text>
                    </p>

                    {blockedReason === 'underage' && (
                        <div class='alert alert-info py-2 px-3 small'>
                            <Text id='faceConsent.underage'>
                                You must be 18 to consent to this. A face template counts as a
                                special category of personal data, and the age limit for those
                                is 18.
                            </Text>
                        </div>
                    )}

                    {blockedReason === 'missingBirth' && (
                        <div class='alert alert-info py-2 px-3 small'>
                            <Text id='faceConsent.missingBirth'>
                                Add your date of birth to your profile first — we need it to
                                confirm you are old enough to consent.
                            </Text>
                        </div>
                    )}

                    {deleted && (
                        <div class='alert alert-success py-2 px-3 small'>
                            <Text id='faceConsent.deleted'>Your face data has been deleted.</Text>
                        </div>
                    )}

                    {/*
                        A 200 with vectorRemoved false: our records are erased but the
                        Rekognition vector was not confirmed removed. Saying "deleted"
                        here would be a false erasure claim.
                    */}
                    {vectorPending && (
                        <div class='alert alert-warning py-2 px-3 small'>
                            <Text id='faceConsent.deletedPending'>
                                Your face data has been removed from our records. Deletion at the
                                face-recognition service is not confirmed yet — we finish it
                                automatically. Contact us if you want it verified.
                            </Text>
                        </div>
                    )}

                    {errorKey && (
                        <div class='alert alert-danger py-2 px-3 small'>
                            {errorText(errorKey)}
                        </div>
                    )}

                    {confirm === 'withdraw' && (
                        <div class='alert alert-warning py-2 px-3 small'>
                            <p class='mb-2'>
                                <Text id='faceConsent.confirmWithdraw'>
                                    Switching off the last option deletes your face template and
                                    everything built from it. This cannot be undone — you would
                                    have to enrol again from scratch.
                                </Text>
                            </p>
                            <button
                                type='button'
                                class='btn btn-danger btn-sm me-2'
                                disabled={saving}
                                onClick={this.confirmPending}
                            >
                                <Text id='faceConsent.confirmWithdrawYes'>Yes, delete my face data</Text>
                            </button>
                            <button
                                type='button'
                                class='btn btn-outline-secondary btn-sm'
                                disabled={saving}
                                onClick={this.cancelConfirm}
                            >
                                <Text id='faceConsent.cancel'>Cancel</Text>
                            </button>
                        </div>
                    )}

                    {confirm === 'delete' && (
                        <div class='alert alert-warning py-2 px-3 small'>
                            <p class='mb-2'>
                                <Text id='faceConsent.confirmDelete'>
                                    This deletes your face template and everything built from it.
                                    This cannot be undone — you would have to enrol again from
                                    scratch.
                                </Text>
                            </p>
                            <button
                                type='button'
                                class='btn btn-danger btn-sm me-2'
                                disabled={saving}
                                onClick={this.confirmPending}
                            >
                                <Text id='faceConsent.confirmDeleteYes'>Yes, delete my face data</Text>
                            </button>
                            <button
                                type='button'
                                class='btn btn-outline-secondary btn-sm'
                                disabled={saving}
                                onClick={this.cancelConfirm}
                            >
                                <Text id='faceConsent.cancel'>Cancel</Text>
                            </button>
                        </div>
                    )}

                    {/*
                        Gated on profile.enrolled ONLY — not on !blockedReason. Withdrawal must
                        stay reachable even if the age gate later refuses this user (e.g. their
                        birth date is cleared or becomes unparseable after enrolment): the switches
                        and the delete button are how consent gets withdrawn, and Art. 9 consent
                        must remain revocable at any time regardless of the age check's current
                        verdict. Only the ENROL path below is gated on !blockedReason.
                    */}
                    {profile.enrolled && (
                        <>
                            <div class='form-check form-switch mb-2'>
                                <input
                                    class='form-check-input'
                                    type='checkbox'
                                    id='faceFindMyPhotos'
                                    checked={profile.allowFindMyPhotos}
                                    disabled={saving}
                                    onChange={e => this.requestSwitch('allowFindMyPhotos', e.target.checked)}
                                />
                                <label class='form-check-label' for='faceFindMyPhotos'>
                                    <Text id='faceConsent.findMyPhotos'>Find photos of me</Text>
                                    <span class='d-block text-body-secondary small'>
                                        <Text id='faceConsent.findMyPhotosHelp'>
                                            Not in use yet. When the feature launches, your face
                                            template is compared against photos in the feed and you
                                            get to see which photos you appear in. Only you see
                                            these results. If you switch it on now, your consent
                                            applies from launch.
                                        </Text>
                                    </span>
                                </label>
                            </div>

                            <div class='form-check form-switch mb-3'>
                                <input
                                    class='form-check-input'
                                    type='checkbox'
                                    id='faceTagSuggestions'
                                    checked={profile.allowTagSuggestions}
                                    disabled={saving}
                                    onChange={e => this.requestSwitch('allowTagSuggestions', e.target.checked)}
                                />
                                <label class='form-check-label' for='faceTagSuggestions'>
                                    <Text id='faceConsent.tagSuggestions'>Let others suggest tagging me</Text>
                                    <span class='d-block text-body-secondary small'>
                                        <Text id='faceConsent.tagSuggestionsHelp'>
                                            Not in use yet. When the feature launches, someone
                                            posting a photo may be offered your name. They must
                                            confirm it before you are tagged, and you are notified.
                                            If you switch it on now, your consent applies from
                                            launch.
                                        </Text>
                                    </span>
                                </label>
                            </div>

                            <button
                                type='button'
                                class='btn btn-outline-danger btn-sm'
                                disabled={saving}
                                onClick={this.requestDelete}
                            >
                                <Text id='faceConsent.delete'>Delete my face data</Text>
                            </button>
                        </>
                    )}

                    {!blockedReason && !profile.enrolled && (
                        <>
                            <p class='small mb-2'>
                                <Text id='faceConsent.pickImages' fields={{ max: MAX_ENROLL_IMAGES }}>
                                    Choose up to {MAX_ENROLL_IMAGES} photos showing only you.
                                </Text>
                            </p>
                            <div class='d-flex flex-wrap gap-2 mb-3'>
                                {images.map(image => (
                                    <button
                                        type='button'
                                        key={image.newFilename}
                                        class={`btn p-0 border ${selected.includes(image.newFilename) ? 'border-primary border-3' : 'border-secondary'}`}
                                        onClick={() => this.toggleImage(image.newFilename)}
                                    >
                                        <img
                                            src={image.s3SmallLink}
                                            alt=''
                                            style='width: 64px; height: 64px; object-fit: cover;'
                                        />
                                    </button>
                                ))}
                            </div>
                            {images.length === 0 && (
                                <div class='alert alert-info py-2 px-3 small'>
                                    <Text id='faceConsent.noImages'>
                                        Upload a photo of yourself first.
                                    </Text>
                                </div>
                            )}
                            {/*
                                All three consent buttons are disabled until at least one photo
                                is picked. Without this line the panel is a dead end: three greyed
                                buttons and no stated reason, which reads as "the feature is
                                broken" rather than "your turn".
                            */}
                            {images.length > 0 && (
                                <p class='small text-body-secondary mb-2'>
                                    {selected.length === 0 ? (
                                        <Text id='faceConsent.selectToContinue'>
                                            Tap a photo above to select it, then choose what to turn on.
                                        </Text>
                                    ) : (
                                        <Text
                                            id='faceConsent.selectedCount'
                                            fields={{ count: selected.length, max: MAX_ENROLL_IMAGES }}
                                        >
                                            {selected.length} of {MAX_ENROLL_IMAGES} photos selected.
                                        </Text>
                                    )}
                                </p>
                            )}
                            <button
                                type='button'
                                class='btn btn-primary btn-sm me-2'
                                disabled={saving || !canSubmit({
                                    blockedReason,
                                    selected,
                                    switches: { allowFindMyPhotos: true, allowTagSuggestions: false },
                                })}
                                onClick={() => this.enroll({ allowFindMyPhotos: true, allowTagSuggestions: false })}
                            >
                                <Text id='faceConsent.enrolFindOnly'>Turn on “Find photos of me”</Text>
                            </button>
                            <button
                                type='button'
                                class='btn btn-outline-primary btn-sm me-2'
                                disabled={saving || !canSubmit({
                                    blockedReason,
                                    selected,
                                    switches: { allowFindMyPhotos: false, allowTagSuggestions: true },
                                })}
                                onClick={() => this.enroll({ allowFindMyPhotos: false, allowTagSuggestions: true })}
                            >
                                <Text id='faceConsent.enrolTagOnly'>Turn on only “Let others suggest tagging me”</Text>
                            </button>
                            <button
                                type='button'
                                class='btn btn-outline-primary btn-sm'
                                disabled={saving || !canSubmit({
                                    blockedReason,
                                    selected,
                                    switches: { allowFindMyPhotos: true, allowTagSuggestions: true },
                                })}
                                onClick={() => this.enroll({ allowFindMyPhotos: true, allowTagSuggestions: true })}
                            >
                                <Text id='faceConsent.enrolBoth'>Turn on both</Text>
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default ProfileFaceConsent;
